// Column catalogue for the rollout master grid (spec §7.1).
//
// 30 columns across 9 top-level groups (OrganicAssets added assetsOrganic +
// metaOrganicBuilt). Two top groups (Budget, Execution)
// carry sub-groups (Meta / Google) that the 3-tier header renders between the
// top group and the column label. The other 7 top groups are flat -- their
// header cell spans two rows visually.
//
// User-customisable layout: visibility per column, and the ORDER of the 9
// top-level groups. Column order WITHIN a group is fixed (factory order),
// and sub-group order (Meta before Google) is fixed. The group-reorder UX
// is a grid-level modal (GroupReorderModal.tsx); per-column reorder is
// intentionally gone (users kept moving Meta Monthly after Google Monthly
// etc.).
//
// Preset wire shape unchanged (`visibleColumns: string[]`). On read we
// canonicalise any stored order to factory-within-group via canonicalizeOrder
// -- a PURE render-time transform (no write-back to Firestore). Presets
// saved before commit N with per-column reordering inside a group are
// silently snapped on first load.

import type { AccountCampaign } from '../../types/accountCampaign';

export type ColumnKey =
  // Identification
  | 'location'
  | 'assignedAm'
  | 'campaign'
  // Multi Lead Form spec §3.1: Lead Form selector cell sits next to the
  // Campaign column in the Identification group — both pick "what this
  // row is doing" rather than budget arithmetic. Single-form rows render
  // static text; multi-form rows expose a dropdown; orphan rows surface
  // a red warning + reassign prompt.
  | 'leadForm'
  // Ad Accounts
  | 'adAccountMeta'
  | 'adAccountGoogle'
  // Dates
  | 'startDate'
  | 'endDate'
  // Budget > Meta. metaType is the per-row Meta ad type override
  // (Pre-P2b-3 polish round 5 §1 + follow-up). Sits in the budget.meta
  // sub-group for visual clustering with the other Meta columns —
  // semantically it's a setup field, but adjacency is what scanning
  // AMs need.
  | 'metaType'
  | 'metaMonthly'
  | 'metaIncrease'
  | 'metaDaily'
  // Budget > Google
  | 'googleMonthly'
  | 'googleIncrease'
  | 'googleDaily'
  // Ready to Build
  | 'metaReady'
  | 'googleReady'
  // Status
  | 'status'
  // Execution > Meta
  | 'metaBuilt'
  | 'metaLocalBuilt'
  // OrganicAssets spec §5.1 / §6.2: mirror of metaLocalBuilt for the Organic
  // asset type. Gated on hasOrganicFiles the same way Local Built is gated on
  // hasLocalFiles.
  | 'metaOrganicBuilt'
  | 'metaLive'
  | 'metaPostCheck'
  // Execution > Google
  | 'googleBuilt'
  | 'googleScheduled'
  | 'googlePostCheck'
  // Assets (order: National → Local → Organic → Google, spec §6.1)
  | 'assetsMeta'
  | 'assetsLocal'
  | 'assetsOrganic'
  | 'assetsGoogle'
  // Other
  | 'rowNotes'
  | 'actions';

export type TopGroupKey =
  | 'identification'
  | 'adAccounts'
  | 'dates'
  | 'budget'
  | 'ready'
  | 'status'
  | 'execution'
  | 'assets'
  | 'other';

export type SubGroupKey = 'budget.meta' | 'budget.google' | 'execution.meta' | 'execution.google';

export type ColumnDef = {
  key: ColumnKey;
  label: string;
  topGroup: TopGroupKey;
  subGroup: SubGroupKey | null;
};

export type TopGroupDef = {
  key: TopGroupKey;
  label: string;
  // If the group has sub-groups, listed here in factory sub-group order.
  subGroups: SubGroupDef[];
  // Alternating background shading (per top group, not per sub-group --
  // sub-groups inherit the parent's shade).
  shaded: boolean;
};

export type SubGroupDef = {
  key: SubGroupKey;
  label: string;
};

// ---------- factory data ----------

export const TOP_GROUPS: TopGroupDef[] = [
  { key: 'identification', label: 'Identification', subGroups: [], shaded: false },
  { key: 'adAccounts', label: 'Ad Accounts', subGroups: [], shaded: true },
  { key: 'dates', label: 'Dates', subGroups: [], shaded: false },
  {
    key: 'budget',
    label: 'Budget',
    subGroups: [
      { key: 'budget.meta', label: 'Meta' },
      { key: 'budget.google', label: 'Google' },
    ],
    shaded: true,
  },
  { key: 'ready', label: 'Ready to Build', subGroups: [], shaded: false },
  { key: 'status', label: 'Status', subGroups: [], shaded: true },
  {
    key: 'execution',
    label: 'Execution',
    subGroups: [
      { key: 'execution.meta', label: 'Meta' },
      { key: 'execution.google', label: 'Google' },
    ],
    shaded: false,
  },
  { key: 'assets', label: 'Assets', subGroups: [], shaded: true },
  { key: 'other', label: 'Other', subGroups: [], shaded: false },
];

export const COLUMNS: ColumnDef[] = [
  // Identification
  { key: 'location', label: 'Location', topGroup: 'identification', subGroup: null },
  { key: 'assignedAm', label: 'AM', topGroup: 'identification', subGroup: null },
  { key: 'campaign', label: 'Campaign', topGroup: 'identification', subGroup: null },
  // Multi Lead Form spec §3.1.
  { key: 'leadForm', label: 'Lead Form', topGroup: 'identification', subGroup: null },
  // Ad Accounts
  { key: 'adAccountMeta', label: 'Meta', topGroup: 'adAccounts', subGroup: null },
  { key: 'adAccountGoogle', label: 'Google', topGroup: 'adAccounts', subGroup: null },
  // Dates
  { key: 'startDate', label: 'Start', topGroup: 'dates', subGroup: null },
  { key: 'endDate', label: 'End', topGroup: 'dates', subGroup: null },
  // Budget > Meta
  { key: 'metaType', label: 'Type', topGroup: 'budget', subGroup: 'budget.meta' },
  { key: 'metaMonthly', label: 'Monthly', topGroup: 'budget', subGroup: 'budget.meta' },
  { key: 'metaIncrease', label: 'Increase', topGroup: 'budget', subGroup: 'budget.meta' },
  { key: 'metaDaily', label: 'Daily', topGroup: 'budget', subGroup: 'budget.meta' },
  // Budget > Google
  { key: 'googleMonthly', label: 'Monthly', topGroup: 'budget', subGroup: 'budget.google' },
  { key: 'googleIncrease', label: 'Increase', topGroup: 'budget', subGroup: 'budget.google' },
  { key: 'googleDaily', label: 'Daily', topGroup: 'budget', subGroup: 'budget.google' },
  // Ready to Build
  { key: 'metaReady', label: 'Meta', topGroup: 'ready', subGroup: null },
  { key: 'googleReady', label: 'Google', topGroup: 'ready', subGroup: null },
  // Status
  { key: 'status', label: 'Status', topGroup: 'status', subGroup: null },
  // Execution > Meta
  { key: 'metaBuilt', label: 'Main Built', topGroup: 'execution', subGroup: 'execution.meta' },
  {
    key: 'metaLocalBuilt',
    label: 'Local Built',
    topGroup: 'execution',
    subGroup: 'execution.meta',
  },
  {
    key: 'metaOrganicBuilt',
    label: 'Organic Built',
    topGroup: 'execution',
    subGroup: 'execution.meta',
  },
  // Round-3 polish Fix 3: display label is "Scheduled/Live" — the
  // Meta team uses one checkbox to mark a row as either scheduled or
  // live. Schema field stays metaTurnedOn.
  { key: 'metaLive', label: 'Scheduled/Live', topGroup: 'execution', subGroup: 'execution.meta' },
  {
    key: 'metaPostCheck',
    label: 'Post-Check',
    topGroup: 'execution',
    subGroup: 'execution.meta',
  },
  // Execution > Google
  {
    key: 'googleBuilt',
    label: 'Main Built',
    topGroup: 'execution',
    subGroup: 'execution.google',
  },
  {
    key: 'googleScheduled',
    label: 'Scheduled',
    topGroup: 'execution',
    subGroup: 'execution.google',
  },
  // Round-3 polish Fix 3: display label is "Budget Updated" — the
  // Google team uses this checkbox to mark that the post-go-live
  // budget reconcile is done. Schema field stays
  // googlePostRolloutCheck.
  {
    key: 'googlePostCheck',
    label: 'Budget Updated',
    topGroup: 'execution',
    subGroup: 'execution.google',
  },
  // Assets (order: National → Local → Organic → Google, spec §6.1)
  { key: 'assetsMeta', label: 'Meta', topGroup: 'assets', subGroup: null },
  { key: 'assetsLocal', label: 'Local', topGroup: 'assets', subGroup: null },
  { key: 'assetsOrganic', label: 'Organic', topGroup: 'assets', subGroup: null },
  { key: 'assetsGoogle', label: 'Google', topGroup: 'assets', subGroup: null },
  // Other
  { key: 'rowNotes', label: 'Notes', topGroup: 'other', subGroup: null },
  { key: 'actions', label: 'Actions', topGroup: 'other', subGroup: null },
];

export const FACTORY_ORDER: ColumnKey[] = COLUMNS.map((c) => c.key);
export const ALL_COLUMN_KEYS: Set<ColumnKey> = new Set(FACTORY_ORDER);

const COLUMN_BY_KEY: Map<ColumnKey, ColumnDef> = new Map(COLUMNS.map((c) => [c.key, c]));
const TOP_GROUP_BY_KEY: Map<TopGroupKey, TopGroupDef> = new Map(
  TOP_GROUPS.map((g) => [g.key, g]),
);

export function columnDef(key: ColumnKey): ColumnDef {
  const def = COLUMN_BY_KEY.get(key);
  if (!def) throw new Error(`Unknown column key: ${key}`);
  return def;
}

export function topGroupDef(key: TopGroupKey): TopGroupDef {
  const def = TOP_GROUP_BY_KEY.get(key);
  if (!def) throw new Error(`Unknown top group: ${key}`);
  return def;
}

// Validates + cleans a candidate order array. Rules:
//   1. Drop any unknown column keys (forward/backward-compatible with presets
//      saved before or after a column set change).
//   2. Append any factory columns missing from the candidate, in factory order.
//      This ensures the server and client never drop a column just because a
//      user's preset predates its introduction.
//   3. Deduplicate.
// Returns a tuple of { order, dropped, added } so tests and telemetry can see
// what happened to a stale preset.
export function sanitizeOrder(candidate: readonly string[]): {
  order: ColumnKey[];
  dropped: string[];
  added: ColumnKey[];
} {
  const seen = new Set<ColumnKey>();
  const order: ColumnKey[] = [];
  const dropped: string[] = [];
  for (const k of candidate) {
    if (!ALL_COLUMN_KEYS.has(k as ColumnKey)) {
      dropped.push(k);
      continue;
    }
    if (seen.has(k as ColumnKey)) continue;
    seen.add(k as ColumnKey);
    order.push(k as ColumnKey);
  }
  const added: ColumnKey[] = [];
  for (const k of FACTORY_ORDER) {
    if (!seen.has(k)) {
      order.push(k);
      added.push(k);
    }
  }
  return { order, dropped, added };
}

// Factory-order map: group -> columns in that group, in factory order. Used
// by expandGroupOrder to rebuild a full column list from a group sequence.
const FACTORY_COLUMNS_BY_GROUP: Map<TopGroupKey, ColumnKey[]> = (() => {
  const map = new Map<TopGroupKey, ColumnKey[]>();
  for (const c of COLUMNS) {
    const bucket = map.get(c.topGroup) ?? [];
    bucket.push(c.key);
    map.set(c.topGroup, bucket);
  }
  return map;
})();

// Derive the top-group sequence from a column order by first-occurrence. Any
// groups with no columns represented are appended in factory order, so the
// result always covers all 9 groups in some deterministic sequence.
export function getGroupOrder(order: readonly ColumnKey[]): TopGroupKey[] {
  const seen = new Set<TopGroupKey>();
  const seq: TopGroupKey[] = [];
  for (const k of order) {
    const def = COLUMN_BY_KEY.get(k);
    if (!def) continue;
    if (seen.has(def.topGroup)) continue;
    seen.add(def.topGroup);
    seq.push(def.topGroup);
  }
  // Fill any missing groups in factory order so the consumer (reorder modal,
  // header builder) always has a complete 9-entry list.
  for (const tg of TOP_GROUPS) {
    if (!seen.has(tg.key)) {
      seq.push(tg.key);
      seen.add(tg.key);
    }
  }
  return seq;
}

// Expand a top-group sequence into a full column order. Each group's columns
// appear in factory order (sub-groups Meta before Google, implicit in COLUMNS).
// Unknown groups are silently ignored. Missing groups have their factory
// columns appended in factory group order at the end.
export function expandGroupOrder(groupOrder: readonly TopGroupKey[]): ColumnKey[] {
  const out: ColumnKey[] = [];
  const placed = new Set<TopGroupKey>();
  for (const tg of groupOrder) {
    if (placed.has(tg)) continue;
    const cols = FACTORY_COLUMNS_BY_GROUP.get(tg);
    if (!cols) continue;
    placed.add(tg);
    out.push(...cols);
  }
  for (const tg of TOP_GROUPS) {
    if (placed.has(tg.key)) continue;
    const cols = FACTORY_COLUMNS_BY_GROUP.get(tg.key);
    if (!cols) continue;
    out.push(...cols);
  }
  return out;
}

// Canonicalise a column order: preserve the top-group sequence the user set,
// but force every group's internal columns back to factory order. This is a
// PURE render-time transform -- callers never write the result back to
// Firestore. Presets authored when per-column reordering inside a group was
// supported are silently snapped on first load; the stored envelope stays
// untouched and stable across future changes of this rule.
export function canonicalizeOrder(order: readonly ColumnKey[]): ColumnKey[] {
  return expandGroupOrder(getGroupOrder(order));
}

// Move a top-level group one slot up or down. Returns a fresh column order
// with the new group sequence expanded to factory-within-group. No-op if the
// move would go past the ends.
export function moveGroup(
  order: readonly ColumnKey[],
  groupKey: TopGroupKey,
  direction: 'up' | 'down',
): ColumnKey[] {
  const seq = getGroupOrder(order);
  const idx = seq.indexOf(groupKey);
  if (idx < 0) return [...order];
  const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= seq.length) return [...order];
  const next = [...seq];
  const a = next[idx]!;
  const b = next[swapIdx]!;
  next[idx] = b;
  next[swapIdx] = a;
  return expandGroupOrder(next);
}

// ---------- render helpers ----------

// Given an order + visible-set, produce the header rows. Row 1 groups top-
// level columns with either rowSpan=2 (flat groups) or colSpan covering their
// sub-groups. Row 2 is the sub-group row. Row 3 is individual column labels.
//
// Collapsing all-hidden groups into a thin placeholder (spec item 2) is
// handled at render time by checking whether a top group has >0 visible
// columns; callers render the placeholder element themselves.

export type HeaderRow1Cell = {
  topGroup: TopGroupKey;
  label: string;
  colSpan: number;            // total visible columns inside the top group
  rowSpan: 1 | 2;             // 2 for flat groups, 1 for sub-grouped groups
  shaded: boolean;
};

export type HeaderRow2Cell = {
  subGroup: SubGroupKey;
  label: string;
  colSpan: number;
  shaded: boolean;
};

export type HeaderRow3Cell = {
  column: ColumnKey;
  label: string;
  shaded: boolean;
  subGroup: SubGroupKey | null;
  topGroup: TopGroupKey;
};

export type BuiltHeader = {
  row1: HeaderRow1Cell[];
  row2: HeaderRow2Cell[];
  row3: HeaderRow3Cell[];
  // Columns actually rendered in the body, in final display order.
  orderedColumns: ColumnKey[];
};

export function buildHeader(
  order: readonly ColumnKey[],
  visibleColumns: ReadonlySet<ColumnKey>,
): BuiltHeader {
  // Keep only visible columns, preserving order.
  const visible = order.filter((k) => visibleColumns.has(k));
  const row1: HeaderRow1Cell[] = [];
  const row2: HeaderRow2Cell[] = [];
  const row3: HeaderRow3Cell[] = [];

  // Walk the visible list and group consecutive cells by topGroup + subGroup.
  // Gaps (e.g. a user rearranges Budget > Meta between Execution cells) would
  // violate our reorder constraints -- sanitizeOrder and moveColumn prevent
  // that -- so consecutive grouping is safe.
  let i = 0;
  while (i < visible.length) {
    const startKey = visible[i]!;
    const startDef = columnDef(startKey);
    const topGroup = topGroupDef(startDef.topGroup);
    // Walk forward collecting columns in this top group.
    let j = i;
    while (j < visible.length && columnDef(visible[j]!).topGroup === topGroup.key) j++;
    const tgCols = visible.slice(i, j);

    if (topGroup.subGroups.length === 0) {
      row1.push({
        topGroup: topGroup.key,
        label: topGroup.label,
        colSpan: tgCols.length,
        rowSpan: 2,
        shaded: topGroup.shaded,
      });
      for (const key of tgCols) {
        const def = columnDef(key);
        row3.push({
          column: key,
          label: def.label,
          shaded: topGroup.shaded,
          subGroup: null,
          topGroup: topGroup.key,
        });
      }
    } else {
      row1.push({
        topGroup: topGroup.key,
        label: topGroup.label,
        colSpan: tgCols.length,
        rowSpan: 1,
        shaded: topGroup.shaded,
      });
      // Sub-group walk: consecutive cells with the same subGroup within the
      // top group become one sub-group header cell.
      let k = 0;
      while (k < tgCols.length) {
        const colKey = tgCols[k]!;
        const def = columnDef(colKey);
        const sg = def.subGroup;
        if (sg === null) {
          // Shouldn't happen: subgrouped top group should have every column
          // subgrouped. Skip defensively.
          k++;
          continue;
        }
        let m = k;
        while (m < tgCols.length && columnDef(tgCols[m]!).subGroup === sg) m++;
        const subCols = tgCols.slice(k, m);
        const sgDef = topGroup.subGroups.find((s) => s.key === sg);
        row2.push({
          subGroup: sg,
          label: sgDef?.label ?? sg,
          colSpan: subCols.length,
          shaded: topGroup.shaded,
        });
        for (const subKey of subCols) {
          const subDef = columnDef(subKey);
          row3.push({
            column: subKey,
            label: subDef.label,
            shaded: topGroup.shaded,
            subGroup: sg,
            topGroup: topGroup.key,
          });
        }
        k = m;
      }
    }
    i = j;
  }

  return { row1, row2, row3, orderedColumns: visible };
}

// Convenience: given a top-group key, the shaded class for a cell. Keep as a
// single source of truth so header + body cells don't drift.
export function cellShadeClass(topGroup: TopGroupKey): string {
  const def = TOP_GROUP_BY_KEY.get(topGroup);
  return def?.shaded ? 'bg-slate-50' : '';
}

// ---------- row-level helpers used by MasterGrid body ----------

// The Local / Organic Built checkboxes render only when there's a meaningful
// build state. OrganicAssets spec §5.1/§5.3: file PRESENCE is the trigger —
// the checkbox appears once the folder holds files (State 3/4), not merely
// when a URL is configured (State 2 = empty folder shows no checkbox). A row
// already marked built keeps its (checked) box even if files later vanish, so
// the AM can untick. Legacy rows read hasLocalFiles / hasOrganicFiles as
// undefined → false until the first folder refresh (C11 auto-refresh on grid
// load), so they self-heal.
export function shouldRenderMetaLocalBuiltCell(
  row: Pick<AccountCampaign, 'hasLocalFiles' | 'metaLocalBuilt'>,
): boolean {
  return (row.hasLocalFiles ?? false) || row.metaLocalBuilt === true;
}

export function shouldRenderMetaOrganicBuiltCell(
  row: Pick<AccountCampaign, 'hasOrganicFiles' | 'metaOrganicBuilt'>,
): boolean {
  return (row.hasOrganicFiles ?? false) || row.metaOrganicBuilt === true;
}
